import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { RefreshCw } from "lucide-react";
import { ak } from "./admin-styles";
import { SyncLogTable } from "./sync-parts";
import { instanceStatus } from "./sync-status";
import { api } from "~/lib/api";
import { Banner, Modal, PageHead, Section, Status, Time, Toggle } from "~/components/editorial";

interface JiraInstance {
  id: string;
  name: string;
  baseUrl: string;
  projectCount: number;
  connectedAt: string;
  lastSyncAt: string | null;
  syncStatus: "idle" | "syncing" | "error" | "pending";
  syncProgress?: number;
  syncMessage?: string | null;
  syncError?: string | null;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
  instanceName: string;
  avatarUrl: string | null;
  deletedAt: string | null;
  enabled: boolean;
  issueCount: number;
  sprintCount: number;
  totalPoints: number;
  resolvedCount: number;
  resolvedPoints: number;
  inProgressCount: number;
  inProgressPoints: number;
}

interface SyncLog {
  id: string;
  sourceName: string;
  syncType: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  duration: number | null;
  itemsSynced: number;
  errorMessage: string | null;
  details: Record<string, unknown> | null;
}

export function JiraSettingsPage() {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState<string | null>(null); // instance ID
  const [showSyncLogs, setShowSyncLogs] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showDeletedProjects, setShowDeletedProjects] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    baseUrl: "",
    email: "",
    apiToken: "",
  });

  // Update credentials form state
  const [updateFormData, setUpdateFormData] = useState({
    email: "",
    apiToken: "",
  });

  const queryClient = useQueryClient();
  const instancesQuery = useQuery({
    queryKey: ["jira-instances"],
    queryFn: async () =>
      (await api.get<{ data: JiraInstance[] }>("/jira/instances")).data,
    staleTime: 0,
    // Poll for updates while any instance is syncing
    refetchInterval: (query) =>
      query.state.data?.some(
        (inst) =>
          inst.syncStatus === "syncing" || inst.syncStatus === "pending",
      )
        ? 2000
        : false,
  });
  const instances = instancesQuery.data ?? [];
  const loading = instancesQuery.isLoading;
  const fetchInstances = () => instancesQuery.refetch();
  const setInstances = (update: (prev: JiraInstance[]) => JiraInstance[]) =>
    queryClient.setQueryData<JiraInstance[]>(["jira-instances"], (prev) =>
      update(prev ?? []),
    );

  const { data: projects = [] } = useQuery({
    queryKey: ["jira-projects-admin"],
    // Include disabled projects in admin panel so they can be re-enabled
    queryFn: async () =>
      (
        await api.get<{ data: JiraProject[] }>(
          "/jira/projects/overview?includeDisabled=true",
        )
      ).data,
    staleTime: 0,
  });
  const setProjects = (update: (prev: JiraProject[]) => JiraProject[]) =>
    queryClient.setQueryData<JiraProject[]>(["jira-projects-admin"], (prev) =>
      update(prev ?? []),
    );

  const syncLogsQuery = useQuery({
    queryKey: ["jira-sync-logs"],
    queryFn: async () =>
      (await api.get<{ data: SyncLog[] }>("/jira/sync-logs?limit=20")).data,
    staleTime: 0,
  });
  const syncLogs = syncLogsQuery.data ?? [];
  const fetchSyncLogs = () => syncLogsQuery.refetch();

  const handleToggleProjectEnabled = async (
    projectId: string,
    currentEnabled: boolean,
  ) => {
    // Optimistic update
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, enabled: !currentEnabled } : p,
      ),
    );

    try {
      await api.patch(`/jira/projects/${projectId}`, {
        enabled: !currentEnabled,
      });
    } catch (err) {
      // Revert on error
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, enabled: currentEnabled } : p,
        ),
      );
      console.error("Failed to toggle project enabled:", err);
      setError("Failed to update project settings");
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setConnecting(true);
    setError(null);

    try {
      // Normalize the base URL
      let baseUrl = formData.baseUrl.trim();
      if (!baseUrl.startsWith("http")) {
        baseUrl = "https://" + baseUrl;
      }
      baseUrl = baseUrl.replace(/\/$/, ""); // Remove trailing slash

      await api.post("/jira/instances", {
        name: formData.name || baseUrl.replace("https://", "").split(".")[0],
        baseUrl,
        email: formData.email,
        apiToken: formData.apiToken,
      });

      setSuccess("JIRA instance connected. Initial sync is starting...");
      setShowConnectModal(false);
      setFormData({ name: "", baseUrl: "", email: "", apiToken: "" });
      fetchInstances();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to connect JIRA instance",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (instanceId: string, full = false) => {
    // Optimistically update state immediately
    setInstances((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? { ...inst, syncStatus: "pending" as const }
          : inst,
      ),
    );

    try {
      await api.post(
        `/jira/instances/${instanceId}/sync${full ? "?full=true" : ""}`,
      );
      // Immediately fetch to get latest status
      fetchInstances();
    } catch (err) {
      console.error("Failed to trigger sync:", err);
      // Revert on error
      fetchInstances();
    }
  };

  const handleDelete = async (instanceId: string) => {
    if (
      !confirm(
        "Are you sure you want to disconnect this JIRA instance? All synced data will be deleted.",
      )
    ) {
      return;
    }
    try {
      await api.delete(`/jira/instances/${instanceId}`);
      setInstances((prev) => prev.filter((inst) => inst.id !== instanceId));
    } catch (err) {
      console.error("Failed to delete instance:", err);
    }
  };

  const handleUpdateCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showUpdateModal) return;

    setUpdating(true);
    setError(null);

    try {
      await api.patch(`/jira/instances/${showUpdateModal}`, {
        email: updateFormData.email || undefined,
        apiToken: updateFormData.apiToken || undefined,
      });

      setSuccess("Credentials updated.");
      setShowUpdateModal(null);
      setUpdateFormData({ email: "", apiToken: "" });
      fetchSyncLogs();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update credentials",
      );
    } finally {
      setUpdating(false);
    }
  };

  const activeProjects = projects.filter((p) => !p.deletedAt);
  const deletedProjects = projects.filter((p) => p.deletedAt);
  const quietProjects = activeProjects.filter((p) => p.issueCount === 0);
  const enabledProjects = activeProjects.filter((p) => p.enabled).length;
  const failingInstances = instances.filter(
    (i) => i.syncStatus === "error",
  ).length;

  // Sort and filter projects
  const displayProjects = (() => {
    let displayList = showAllProjects
      ? activeProjects
      : activeProjects.filter((p) => p.issueCount > 0).slice(0, 15);
    // Add deleted projects at the bottom if toggle is on
    if (showDeletedProjects && deletedProjects.length > 0) {
      displayList = [...displayList, ...deletedProjects];
    }
    return displayList;
  })();

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · Jira"
        title="Jira"
        dek={
          loading ? (
            "Instances, project sync and history."
          ) : instances.length === 0 ? (
            "No Jira instances connected yet."
          ) : (
            <>
              <span className={ak.mono}>{instances.length}</span>{" "}
              {instances.length === 1 ? "instance" : "instances"} connected,
              syncing <span className={ak.mono}>{enabledProjects}</span> of{" "}
              <span className={ak.mono}>{activeProjects.length}</span> projects.{" "}
              {failingInstances === 0 ? (
                "No sync is failing."
              ) : (
                <>
                  <span className={ak.mono}>{failingInstances}</span>{" "}
                  {failingInstances === 1 ? "is" : "are"} failing.
                </>
              )}
            </>
          )
        }
      />

      {success && <Banner>{success}</Banner>}

      {error && (
        <Banner tone="caution" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Section
        kicker="I. Instances"
        title="Connected Jira instances"
        actions={
          <button className={ak.btn} onClick={() => setShowConnectModal(true)}>
            Connect instance
          </button>
        }
      >
        {loading ? (
          <div className={ak.loading}>Loading…</div>
        ) : instances.length > 0 ? (
          <div className={ak.tableWrap}>
            <table className={cx(ak.table, styles.detailTable)}>
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Status</th>
                  <th data-num>Projects</th>
                  <th>Last synced</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {instances.map((instance) => {
                  const st = instanceStatus(instance);
                  const busy =
                    instance.syncStatus === "syncing" ||
                    instance.syncStatus === "pending";
                  const hasDetail =
                    instance.syncStatus === "syncing" ||
                    (instance.syncStatus === "error" && !!instance.syncError);
                  return (
                    <Fragment key={instance.id}>
                      <tr data-has-detail={hasDetail || undefined}>
                        <td>
                          <div className={styles.name}>{instance.name}</div>
                          <a
                            href={instance.baseUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx(ak.link, ak.mono, styles.sub)}
                          >
                            {instance.baseUrl.replace(/^https?:\/\//, "")} ↗
                          </a>
                        </td>
                        <td>
                          <Status kind={st.kind}>{st.label}</Status>
                        </td>
                        <td data-num>{instance.projectCount}</td>
                        <td>
                          {instance.lastSyncAt ? (
                            <Time iso={instance.lastSyncAt} />
                          ) : (
                            <span className={ak.muted}>never</span>
                          )}
                        </td>
                        <td>
                          <div className={ak.actions}>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(instance.id)}
                              disabled={busy}
                              title="Quick sync: last 7 days"
                            >
                              <RefreshCw
                                size={12}
                                className={
                                  instance.syncStatus === "syncing"
                                    ? ak.spinning
                                    : ""
                                }
                              />
                              Sync
                            </button>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(instance.id, true)}
                              disabled={busy}
                              title="Full sync: last 180 days, adds to existing data"
                            >
                              Full
                            </button>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => setShowUpdateModal(instance.id)}
                              title="Update credentials"
                            >
                              Credentials
                            </button>
                            <button
                              className={cx(ak.btnDanger, ak.btnSm)}
                              onClick={() => handleDelete(instance.id)}
                              disabled={instance.syncStatus === "syncing"}
                              title="Disconnect"
                            >
                              Disconnect
                            </button>
                          </div>
                        </td>
                      </tr>
                      {hasDetail && (
                        <tr data-detail>
                          <td colSpan={5}>
                            {instance.syncStatus === "syncing" && (
                              <div className={styles.progressBlock}>
                                <div className={ak.progress}>
                                  <div
                                    style={{
                                      width: `${instance.syncProgress || 0}%`,
                                    }}
                                  />
                                </div>
                                <div>
                                  {instance.syncMessage || "Syncing…"}{" "}
                                  <span className={ak.mono}>
                                    {instance.syncProgress || 0}%
                                  </span>
                                </div>
                              </div>
                            )}
                            {instance.syncStatus === "error" &&
                              instance.syncError && (
                                <Status kind="fail" className={styles.wrap}>
                                  {instance.syncError}
                                </Status>
                              )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={ak.empty}>
            <p>No Jira instances connected.</p>
            <p>Connect one to start counting sprints, tickets and points.</p>
          </div>
        )}

        <p className={ak.note}>
          ● connected, ◆ syncing, ◇ queued, ▲ failing. A full sync reads issues
          updated in the last 180 days and adds to existing data; it never
          clears old records.
        </p>
      </Section>

      {/* Projects Overview */}
      {projects.length > 0 && (
        <Section
          kicker="II. Projects"
          title="Synced projects"
          dek={
            <>
              <span className={ak.mono}>{activeProjects.length}</span> active
              projects,{" "}
              <span className={ak.mono}>
                {activeProjects
                  .reduce((s, p) => s + p.issueCount, 0)
                  .toLocaleString()}
              </span>{" "}
              issues and{" "}
              <span className={ak.mono}>
                {activeProjects
                  .reduce((s, p) => s + p.totalPoints, 0)
                  .toLocaleString()}
              </span>{" "}
              story points.
              {quietProjects.length > 0 && (
                <>
                  {" "}
                  <span className={ak.mono}>{quietProjects.length}</span> have
                  no recent issues.
                </>
              )}
              {deletedProjects.length > 0 && (
                <>
                  {" "}
                  <span className={ak.mono}>{deletedProjects.length}</span> were
                  deleted in Jira.
                </>
              )}
            </>
          }
          actions={
            <>
              {deletedProjects.length > 0 && (
                <button
                  className={cx(ak.btnSecondary, ak.btnSm)}
                  onClick={() => setShowDeletedProjects(!showDeletedProjects)}
                >
                  {showDeletedProjects
                    ? "Hide deleted"
                    : `Show ${deletedProjects.length} deleted`}
                </button>
              )}
              <button
                className={cx(ak.btnSecondary, ak.btnSm)}
                onClick={() => setShowAllProjects(!showAllProjects)}
              >
                {showAllProjects
                  ? "Active only"
                  : `Show all ${activeProjects.length}`}
              </button>
            </>
          }
        >
          <div className={ak.tableWrap}>
            <table className={ak.table}>
              <thead>
                <tr>
                  <th>Sync</th>
                  <th>Key</th>
                  <th>Name</th>
                  <th data-num>Issues</th>
                  <th data-num>Points</th>
                  <th data-num>Done</th>
                  <th data-num>Done pts</th>
                  <th data-num>In progress</th>
                  <th data-num>In prog. pts</th>
                </tr>
              </thead>
              <tbody>
                {displayProjects.map((project) => (
                  <tr
                    key={project.id}
                    className={
                      project.deletedAt ||
                      !project.enabled ||
                      project.issueCount === 0
                        ? ak.rowMuted
                        : undefined
                    }
                  >
                    <td>
                      <Toggle
                        checked={project.enabled}
                        disabled={!!project.deletedAt}
                        onChange={() =>
                          handleToggleProjectEnabled(
                            project.id,
                            project.enabled,
                          )
                        }
                        label={
                          project.enabled
                            ? `Stop syncing ${project.key}`
                            : `Sync ${project.key}`
                        }
                      />
                    </td>
                    <td className={ak.mono}>
                      {project.key}
                      {project.deletedAt && (
                        <span className={styles.deleted}> deleted</span>
                      )}
                    </td>
                    <td>{project.name}</td>
                    <td data-num>{project.issueCount || "–"}</td>
                    <td data-num>{project.totalPoints || "–"}</td>
                    <td data-num>{project.resolvedCount || "–"}</td>
                    <td data-num>{project.resolvedPoints || "–"}</td>
                    <td data-num>{project.inProgressCount || "–"}</td>
                    <td data-num>{project.inProgressPoints || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={ak.note}>
            Issue and point counts cover what Aura has synced, not the whole
            Jira history. Gray rows are switched off, deleted in Jira or have no
            recent issues.{" "}
            {!showAllProjects &&
              "Showing up to 15 projects with issues; show all to see the rest."}
          </p>
        </Section>
      )}

      {/* Sync History */}
      <Section
        kicker="III. History"
        title="Recent syncs"
        actions={
          <button
            className={cx(ak.btnSecondary, ak.btnSm)}
            onClick={() => setShowSyncLogs(!showSyncLogs)}
          >
            {showSyncLogs ? "Hide history" : "Show history"}
          </button>
        }
      >
        {showSyncLogs && <SyncLogTable logs={syncLogs} />}
      </Section>

      <Section kicker="IV. Setup" title="Connecting an instance">
        <ol className={ak.steps}>
          <li>
            Open{" "}
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noopener noreferrer"
              className={ak.link}
            >
              id.atlassian.com
            </a>{" "}
            and go to API tokens.
          </li>
          <li>Click Create API token and give it a name you will recognize.</li>
          <li>Copy the token. Atlassian shows it once.</li>
          <li>Click Connect instance above.</li>
          <li>
            Enter your site URL, like{" "}
            <span className={ak.mono}>yourcompany.atlassian.net</span>.
          </li>
          <li>Enter your email and the token.</li>
        </ol>
        <p className={ak.note}>
          <strong>Note:</strong> the token is stored encrypted. Aura reads
          projects, issues, sprints and user names only.
        </p>
      </Section>

      {/* Connect Modal */}
      {showConnectModal && (
        <Modal
          title="Connect a Jira instance"
          onClose={() => setShowConnectModal(false)}
        >
          <form onSubmit={handleConnect}>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-name">
                Instance name (optional)
              </label>
              <input
                id="jira-name"
                type="text"
                placeholder="My Company Jira"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className={ak.input}
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-url">
                Jira site URL
              </label>
              <input
                id="jira-url"
                type="text"
                placeholder="yourcompany.atlassian.net"
                value={formData.baseUrl}
                onChange={(e) =>
                  setFormData({ ...formData, baseUrl: e.target.value })
                }
                className={cx(ak.input, ak.mono)}
                required
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-email">
                Email
              </label>
              <input
                id="jira-email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className={ak.input}
                required
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-token">
                API token
              </label>
              <input
                id="jira-token"
                type="password"
                placeholder="Your Jira API token"
                value={formData.apiToken}
                onChange={(e) =>
                  setFormData({ ...formData, apiToken: e.target.value })
                }
                className={cx(ak.input, ak.mono)}
                required
              />
              <p className={ak.hint}>
                Create one at{" "}
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ak.link}
                >
                  id.atlassian.com
                </a>
                .
              </p>
            </div>
            <div className={ak.modalActions}>
              <button
                type="button"
                className={ak.btnSecondary}
                onClick={() => setShowConnectModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className={ak.btn} disabled={connecting}>
                {connecting ? "Connecting…" : "Connect"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Update Credentials Modal */}
      {showUpdateModal && (
        <Modal
          title="Update credentials"
          onClose={() => setShowUpdateModal(null)}
        >
          <form onSubmit={handleUpdateCredentials}>
            <p className={cx(ak.hint, styles.modalLead)}>
              Swap the Jira email or API token without disconnecting. Leave a
              field empty to keep its current value.
            </p>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-update-email">
                Email (optional)
              </label>
              <input
                id="jira-update-email"
                type="email"
                placeholder="Leave empty to keep current"
                value={updateFormData.email}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    email: e.target.value,
                  })
                }
                className={ak.input}
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="jira-update-token">
                New API token (optional)
              </label>
              <input
                id="jira-update-token"
                type="password"
                placeholder="Leave empty to keep current"
                value={updateFormData.apiToken}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    apiToken: e.target.value,
                  })
                }
                className={cx(ak.input, ak.mono)}
              />
              <p className={ak.hint}>
                Generate a new token at{" "}
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ak.link}
                >
                  id.atlassian.com
                </a>
                .
              </p>
            </div>
            <div className={ak.modalActions}>
              <button
                type="button"
                className={ak.btnSecondary}
                onClick={() => setShowUpdateModal(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={ak.btn}
                disabled={
                  updating ||
                  (!updateFormData.email && !updateFormData.apiToken)
                }
              >
                {updating ? "Updating…" : "Update credentials"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const styles = {
  detailTable: css({
    "& tr[data-has-detail] td": { borderBottom: "none" },
    "& tr[data-detail] td": { pt: "0", pb: "12px", fontSize: "12.5px" },
  }),
  name: css({
    fontFamily: "serif",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.2,
  }),
  sub: css({ fontSize: "11.5px", color: "ed.ink3!" }),
  progressBlock: css({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    color: "ed.ink2",
  }),
  wrap: css({ whiteSpace: "normal" }),
  deleted: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  }),
  modalLead: css({ mb: "14px" }),
};
